import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import {
  Users, UserPlus, Shield, Building2, MapPin, 
  Edit, UserX, UserCheck, ChevronRight, Lock,
  Eye, EyeOff, AlertTriangle, Activity, Clock, Trash2, KeyRound, Briefcase, FileDown
} from 'lucide-react';
import { Textarea } from '../components/ui/textarea';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Access level badge colors
const accessLevelColors = {
  super_admin: 'bg-red-100 text-red-700 border-red-200',
  admin: 'bg-purple-100 text-purple-700 border-purple-200',
  manager: 'bg-blue-100 text-blue-700 border-blue-200',
  sales: 'bg-amber-100 text-amber-700 border-amber-200',
  finance: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  it: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  support: 'bg-teal-100 text-teal-700 border-teal-200',
  agent: 'bg-green-100 text-green-700 border-green-200',
  viewer: 'bg-slate-100 text-slate-700 border-slate-200'
};

const defaultPermissions = {
  can_view_all_records: false,
  can_view_team_records: false,
  can_view_own_records: true,
  can_override_deal_ownership: false,
  can_view_protected_deals: false,
  can_access_finance_workspace: false,
  can_receive_notifications: true,
  can_lock_records: false,
  can_approve_access: false
};

export const TeamManagementPage = () => {
  const { user, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUserDialog, setShowUserDialog] = useState(false);
  const [showResetPw, setShowResetPw] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [activityLogs, setActivityLogs] = useState([]);
  const [filter, setFilter] = useState({ department: 'all', access_level: 'all' });

  // Report export dialog
  const [reportUser, setReportUser] = useState(null);
  const [reportFormat, setReportFormat] = useState('xlsx');
  const [reportStart, setReportStart] = useState('');
  const [reportEnd, setReportEnd] = useState('');
  const [reportPreview, setReportPreview] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const [form, setForm] = useState({
    email: '',
    password: '',
    name: '',
    role: 'agent',
    branch: 'main',
    department: '',
    manager_id: '',
    region: '',
    access_level: 'agent',
    data_visibility: 'own_only',
    deal_access_level: 'restricted',
    job_title: '',
    job_description: '',
    responsibilities: '',
    permissions: { ...defaultPermissions }
  });

  useEffect(() => {
    if (isAdmin()) {
      loadData();
    }
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, managersRes, deptRes] = await Promise.all([
        axios.get(`${API}/users`),
        axios.get(`${API}/managers`),
        axios.get(`${API}/departments`)
      ]);
      setUsers(usersRes.data);
      setManagers(managersRes.data);
      setDepartments(deptRes.data.departments || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadActivityLogs = async () => {
    try {
      const res = await axios.get(`${API}/activity-logs?limit=50`);
      setActivityLogs(res.data);
    } catch (error) {
      console.error('Failed to load activity logs');
    }
  };

  const handleCreateUser = async () => {
    if (!form.email || !form.password || !form.name) {
      toast.error('Please fill in required fields');
      return;
    }

    try {
      // Sync role with access_level so they always match
      const payload = { ...form, role: form.access_level };
      await axios.post(`${API}/auth/register`, payload);
      toast.success('User created successfully');
      setShowUserDialog(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleUpdateUser = async () => {
    if (!editingUser) return;

    try {
      const updateData = { ...form, role: form.access_level };
      delete updateData.email;
      delete updateData.password;
      
      await axios.put(`${API}/users/${editingUser.id}`, updateData);
      toast.success('User updated successfully');
      setShowUserDialog(false);
      setEditingUser(null);
      resetForm();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleDeactivateUser = async (userId) => {
    if (!window.confirm('Are you sure you want to deactivate this user?')) return;
    
    try {
      await axios.post(`${API}/users/${userId}/deactivate`);
      toast.success('User deactivated');
      loadData();
    } catch (error) {
      toast.error('Failed to deactivate user');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Permanently delete ${userName}? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success(`${userName} deleted`);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const handleResetPassword = async () => {
    if (!showResetPw || !newPassword) { toast.error('Enter a new password'); return; }
    try {
      await axios.put(`${API}/users/${showResetPw.id}/reset-password`, { new_password: newPassword });
      toast.success(`Password reset for ${showResetPw.name}`);
      setShowResetPw(null); setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleActivateUser = async (userId) => {
    try {
      await axios.post(`${API}/users/${userId}/activate`);
      toast.success('User activated');
      loadData();
    } catch (error) {
      toast.error('Failed to activate user');
    }
  };

  const openReportDialog = async (u) => {
    setReportUser(u);
    setReportFormat('xlsx');
    setReportStart('');
    setReportEnd('');
    setReportPreview(null);
    try {
      const res = await axios.get(`${API}/team-reports/${u.id}/preview`);
      setReportPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load report preview');
    }
  };

  const refreshReportPreview = async () => {
    if (!reportUser) return;
    try {
      const params = new URLSearchParams();
      if (reportStart) params.set('start_date', reportStart);
      if (reportEnd) params.set('end_date', reportEnd);
      const qs = params.toString() ? `?${params.toString()}` : '';
      const res = await axios.get(`${API}/team-reports/${reportUser.id}/preview${qs}`);
      setReportPreview(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to refresh preview');
    }
  };

  const handleDownloadReport = async () => {
    if (!reportUser) return;
    setReportLoading(true);
    try {
      const params = new URLSearchParams({ format: reportFormat });
      if (reportStart) params.set('start_date', reportStart);
      if (reportEnd) params.set('end_date', reportEnd);
      const res = await axios.get(`${API}/team-reports/${reportUser.id}/export?${params.toString()}`, { responseType: 'blob' });
      const blob = new Blob([res.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const safe = (reportUser.name || 'member').replace(/[^a-z0-9]+/gi, '_');
      const today = new Date().toISOString().slice(0, 10).replace(/-/g, '');
      a.href = url;
      a.download = `${safe}_report_${today}.${reportFormat}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${reportUser.name}'s report downloaded`);
      setReportUser(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to download report');
    } finally {
      setReportLoading(false);
    }
  };

  const resetForm = () => {
    setForm({
      email: '',
      password: '',
      name: '',
      role: 'agent',
      branch: 'main',
      department: '',
      manager_id: '',
      region: '',
      access_level: 'agent',
      data_visibility: 'own_only',
      deal_access_level: 'restricted',
      job_title: '',
      job_description: '',
      responsibilities: '',
      permissions: { ...defaultPermissions }
    });
  };

  const openEditDialog = (userToEdit) => {
    setEditingUser(userToEdit);
    setForm({
      ...form,
      name: userToEdit.name,
      role: userToEdit.role,
      branch: userToEdit.branch || 'main',
      department: userToEdit.department || '',
      manager_id: userToEdit.manager_id || '',
      region: userToEdit.region || '',
      access_level: userToEdit.access_level || 'agent',
      data_visibility: userToEdit.data_visibility || 'own_only',
      deal_access_level: userToEdit.deal_access_level || 'restricted',
      job_title: userToEdit.job_title || '',
      job_description: userToEdit.job_description || '',
      responsibilities: userToEdit.responsibilities || '',
      permissions: userToEdit.permissions || { ...defaultPermissions }
    });
    setShowUserDialog(true);
  };

  const filteredUsers = users.filter(u => {
    if (filter.department !== 'all' && u.department !== filter.department) return false;
    if (filter.access_level !== 'all' && u.access_level !== filter.access_level) return false;
    return true;
  });

  if (!isAdmin()) {
    return (
      <div className="p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6 flex items-center gap-3">
            <Shield className="h-8 w-8 text-red-600" />
            <div>
              <h2 className="font-semibold text-red-900">Access Denied</h2>
              <p className="text-sm text-red-700">You don't have permission to access Team Management</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="team-management-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Team Management</h1>
          <p className="text-muted-foreground">Manage users, roles, and permissions</p>
        </div>
        <Button onClick={() => { resetForm(); setEditingUser(null); setShowUserDialog(true); }}>
          <UserPlus className="h-4 w-4 mr-2" /> Add Team Member
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Users className="h-8 w-8 text-blue-600" />
            <div>
              <p className="text-xs text-muted-foreground">Total Users</p>
              <p className="text-2xl font-bold">{users.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Shield className="h-8 w-8 text-purple-600" />
            <div>
              <p className="text-xs text-muted-foreground">Admins</p>
              <p className="text-2xl font-bold">
                {users.filter(u => ['super_admin', 'admin'].includes(u.access_level)).length}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <Building2 className="h-8 w-8 text-green-600" />
            <div>
              <p className="text-xs text-muted-foreground">Departments</p>
              <p className="text-2xl font-bold">{departments.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 flex items-center gap-3">
            <UserX className="h-8 w-8 text-red-600" />
            <div>
              <p className="text-xs text-muted-foreground">Inactive</p>
              <p className="text-2xl font-bold">{users.filter(u => !u.is_active).length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="users" className="space-y-4">
        <TabsList>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="activity" onClick={loadActivityLogs}>Activity Logs</TabsTrigger>
        </TabsList>

        <TabsContent value="users">
          {/* Filters */}
          <div className="flex gap-4 mb-4">
            <Select value={filter.department} onValueChange={(v) => setFilter({...filter, department: v})}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Departments" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filter.access_level} onValueChange={(v) => setFilter({...filter, access_level: v})}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="All Access Levels" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="super_admin">Super Admin</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="manager">Manager</SelectItem>
                <SelectItem value="sales">Sales</SelectItem>
                <SelectItem value="finance">Finance</SelectItem>
                <SelectItem value="it">IT</SelectItem>
                <SelectItem value="support">Support</SelectItem>
                <SelectItem value="agent">Agent</SelectItem>
                <SelectItem value="viewer">Viewer</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Users Table */}
          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Department</TableHead>
                  <TableHead>Manager</TableHead>
                  <TableHead>Data Visibility</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((u) => (
                  <TableRow key={u.id} className={!u.is_active ? 'opacity-50' : ''}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell>
                      <Badge className={accessLevelColors[u.access_level] || accessLevelColors.agent}>
                        {u.access_level?.replace('_', ' ') || 'agent'}
                      </Badge>
                    </TableCell>
                    <TableCell>{u.department || '-'}</TableCell>
                    <TableCell>{u.manager_name || '-'}</TableCell>
                    <TableCell>
                      <span className="text-xs capitalize">{u.data_visibility?.replace('_', ' ') || 'own only'}</span>
                    </TableCell>
                    <TableCell>
                      {u.is_active !== false ? (
                        <Badge variant="success" className="bg-green-100 text-green-700">Active</Badge>
                      ) : (
                        <Badge variant="destructive">Inactive</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => openEditDialog(u)} data-testid={`edit-user-${u.id}`}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      {u.is_active !== false ? (
                        <Button variant="ghost" size="sm" onClick={() => handleDeactivateUser(u.id)} disabled={u.id === user.id}>
                          <UserX className="h-4 w-4 text-red-500" />
                        </Button>
                      ) : (
                        <Button variant="ghost" size="sm" onClick={() => handleActivateUser(u.id)}>
                          <UserCheck className="h-4 w-4 text-green-500" />
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => { setShowResetPw(u); setNewPassword(''); }} data-testid={`reset-pw-${u.id}`}>
                        <KeyRound className="h-4 w-4 text-amber-600" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openReportDialog(u)} data-testid={`download-report-${u.id}`} title="Download Member Report">
                        <FileDown className="h-4 w-4 text-blue-600" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(u.id, u.name)} disabled={u.id === user.id} data-testid={`delete-user-${u.id}`}>
                        <Trash2 className="h-4 w-4 text-red-600" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        <TabsContent value="activity">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-5 w-5" /> Recent Activity
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {activityLogs.map((log, idx) => (
                  <div key={idx} className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
                    <div className="h-10 w-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <Activity className="h-5 w-5 text-blue-600" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-medium">
                        <span className="text-blue-600">{log.user_name}</span>
                        {' '}{log.activity_type}{' '}
                        <span className="text-slate-600">{log.record_type}: {log.record_name || log.record_id?.slice(0,8)}</span>
                      </p>
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {new Date(log.timestamp).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
                {activityLogs.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">No activity logs yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* User Create/Edit Dialog */}
      <Dialog open={showUserDialog} onOpenChange={setShowUserDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit Team Member' : 'Add Team Member'}</DialogTitle>
          </DialogHeader>
          
          <div className="grid grid-cols-2 gap-4">
            {/* Basic Info */}
            <div className="col-span-2">
              <h4 className="font-medium text-sm mb-2 text-muted-foreground flex items-center gap-2">
                <Users className="h-4 w-4" /> Basic Information
              </h4>
            </div>
            
            <div>
              <Label>Full Name *</Label>
              <Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} />
            </div>
            
            {!editingUser && (
              <>
                <div>
                  <Label>Email *</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} />
                </div>
                <div>
                  <Label>Password *</Label>
                  <Input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} />
                </div>
              </>
            )}
            
            <div>
              <Label>Department</Label>
              <Input value={form.department} onChange={(e) => setForm({...form, department: e.target.value})} placeholder="e.g., Sales, Finance" />
            </div>
            
            <div>
              <Label>Region / Location</Label>
              <Input value={form.region} onChange={(e) => setForm({...form, region: e.target.value})} placeholder="e.g., Johannesburg" />
            </div>
            
            <div>
              <Label>Reports To (Manager)</Label>
              <Select value={form.manager_id || 'none'} onValueChange={(v) => setForm({...form, manager_id: v === 'none' ? '' : v})}>
                <SelectTrigger><SelectValue placeholder="Select manager" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Manager</SelectItem>
                  {managers.map(m => <SelectItem key={m.id} value={m.id}>{m.name} ({m.department || 'No dept'})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {editingUser && (
              <>
                <div className="col-span-2 pt-4">
                  <h4 className="font-medium text-sm mb-2 text-muted-foreground flex items-center gap-2">
                    <Briefcase className="h-4 w-4" /> Job Description &amp; Responsibilities
                  </h4>
                </div>
                <div className="col-span-2">
                  <Label>Job Title</Label>
                  <Input value={form.job_title} onChange={(e) => setForm({...form, job_title: e.target.value})} placeholder="e.g., Senior Sales Consultant" data-testid="job-title-input" />
                </div>
                <div className="col-span-2">
                  <Label>Job Description</Label>
                  <Textarea rows={3} value={form.job_description} onChange={(e) => setForm({...form, job_description: e.target.value})} placeholder="What this role does day to day..." data-testid="job-description-input" />
                </div>
                <div className="col-span-2">
                  <Label>Roles &amp; Responsibilities</Label>
                  <Textarea rows={4} value={form.responsibilities} onChange={(e) => setForm({...form, responsibilities: e.target.value})} placeholder="Key responsibilities, KPIs, deliverables — one per line..." data-testid="responsibilities-input" />
                  <p className="text-xs text-muted-foreground mt-1">Job descriptions can be updated anytime as performance and duties evolve.</p>
                </div>
              </>
            )}

            {/* Access Control */}
            <div className="col-span-2 pt-4">
              <h4 className="font-medium text-sm mb-2 text-muted-foreground flex items-center gap-2">
                <Shield className="h-4 w-4" /> Access Control
              </h4>
            </div>
            
            <div>
              <Label>Role</Label>
              <Select value={form.access_level} onValueChange={(v) => setForm({...form, access_level: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="sales">Sales</SelectItem>
                  <SelectItem value="finance">Finance</SelectItem>
                  <SelectItem value="it">IT</SelectItem>
                  <SelectItem value="support">Support</SelectItem>
                  <SelectItem value="agent">Agent</SelectItem>
                  <SelectItem value="viewer">Viewer</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Data Visibility Scope</Label>
              <Select value={form.data_visibility} onValueChange={(v) => setForm({...form, data_visibility: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="own_only">Own Records Only</SelectItem>
                  <SelectItem value="team">Team Records</SelectItem>
                  <SelectItem value="all">All Records</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Deal Access Level</Label>
              <Select value={form.deal_access_level} onValueChange={(v) => setForm({...form, deal_access_level: v})}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full Access</SelectItem>
                  <SelectItem value="restricted">Restricted (Masked Data)</SelectItem>
                  <SelectItem value="view_only">View Only</SelectItem>
                  <SelectItem value="none">No Access</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Permissions */}
            <div className="col-span-2 pt-4">
              <h4 className="font-medium text-sm mb-2 text-muted-foreground flex items-center gap-2">
                <Lock className="h-4 w-4" /> Specific Permissions
              </h4>
            </div>
            
            <div className="col-span-2 grid grid-cols-2 gap-3">
              {Object.entries(form.permissions).map(([key, value]) => (
                <div key={key} className="flex items-center gap-2">
                  <Checkbox 
                    checked={value} 
                    onCheckedChange={(checked) => setForm({
                      ...form, 
                      permissions: {...form.permissions, [key]: checked}
                    })}
                  />
                  <Label className="text-sm font-normal capitalize">
                    {key.replace(/_/g, ' ')}
                  </Label>
                </div>
              ))}
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowUserDialog(false)}>Cancel</Button>
            <Button onClick={editingUser ? handleUpdateUser : handleCreateUser} data-testid="save-user-btn">
              {editingUser ? 'Update' : 'Create'} User
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset Password Dialog */}
      <Dialog open={!!showResetPw} onOpenChange={() => setShowResetPw(null)}>
        <DialogContent className="max-w-sm"><DialogHeader><DialogTitle className="flex items-center gap-2"><KeyRound className="h-5 w-5" /> Reset Password</DialogTitle></DialogHeader>
          <div className="space-y-3 py-3">
            <p className="text-sm text-muted-foreground">Set a new password for <span className="font-semibold text-foreground">{showResetPw?.name}</span> ({showResetPw?.email})</p>
            <div className="space-y-1"><Label>New Password</Label><Input type="password" placeholder="Enter new password" value={newPassword} onChange={e => setNewPassword(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleResetPassword()} data-testid="new-password-input" /></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setShowResetPw(null)}>Cancel</Button><Button onClick={handleResetPassword} data-testid="reset-pw-submit">Reset Password</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Download Team Member Report Dialog */}
      <Dialog open={!!reportUser} onOpenChange={(o) => { if (!o) setReportUser(null); }}>
        <DialogContent className="max-w-lg" data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileDown className="h-5 w-5 text-blue-600" /> Download Team Member Report
            </DialogTitle>
            <DialogDescription>
              Export a member&apos;s tasks, leads, projects, WFH entries, invoices and quotations as Excel or Word.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="rounded-md border bg-slate-50 p-3">
              <p className="text-sm">
                <span className="font-semibold">{reportUser?.name}</span>{' '}
                <span className="text-muted-foreground">· {reportUser?.email}</span>
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                Includes tasks, leads, projects, WFH entries, invoices, quotations and opportunities linked to this member.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Start Date (optional)</Label>
                <Input type="date" value={reportStart} onChange={(e) => setReportStart(e.target.value)} data-testid="report-start-date" />
              </div>
              <div>
                <Label className="text-xs">End Date (optional)</Label>
                <Input type="date" value={reportEnd} onChange={(e) => setReportEnd(e.target.value)} data-testid="report-end-date" />
              </div>
              <div className="col-span-2">
                <Button variant="outline" size="sm" onClick={refreshReportPreview} data-testid="refresh-report-preview">
                  Refresh Preview
                </Button>
              </div>
            </div>

            <div>
              <Label className="text-xs">Format</Label>
              <Select value={reportFormat} onValueChange={setReportFormat}>
                <SelectTrigger data-testid="report-format-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="xlsx">Excel Spreadsheet (.xlsx)</SelectItem>
                  <SelectItem value="docx">Word Document (.docx)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {reportPreview && (
              <div className="rounded-md border p-3">
                <p className="text-xs font-semibold mb-2 text-muted-foreground">Preview — what&apos;s in the report</p>
                <div className="grid grid-cols-2 gap-y-1.5 text-sm">
                  <span>Tasks</span><span className="text-right font-medium">{reportPreview.counts.tasks}</span>
                  <span>Leads</span><span className="text-right font-medium">{reportPreview.counts.leads}</span>
                  <span>Projects</span><span className="text-right font-medium">{reportPreview.counts.projects}</span>
                  <span>WFH Entries</span><span className="text-right font-medium">{reportPreview.counts.wfh}</span>
                  <span>Invoices</span><span className="text-right font-medium">{reportPreview.counts.invoices}</span>
                  <span>Quotations</span><span className="text-right font-medium">{reportPreview.counts.quotations}</span>
                  <span>Opportunities</span><span className="text-right font-medium">{reportPreview.counts.opportunities}</span>
                  <span>Activity Log</span><span className="text-right font-medium">{reportPreview.counts.activity ?? 0}</span>
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportUser(null)}>Cancel</Button>
            <Button onClick={handleDownloadReport} disabled={reportLoading} data-testid="report-download-submit">
              {reportLoading ? 'Generating…' : 'Download Report'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TeamManagementPage;
